import { FormField, Modal, SelectInput, Stack, Table } from 'canoe';
import { Field, FieldAttributes, Form, FormikProvider, useFormik, useFormikContext } from 'formik';
import {
  compose,
  connectWithHammock,
  createCacheKey,
  ENDPOINTS,
  fetchResource,
  useActions,
  useCreatePayload,
} from 'hammock';
import debounce from 'lodash/debounce';
import React, { useCallback, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import * as Yup from 'yup';

import { ContentMetadata, ContentSegments } from '@candu-ai/content/types';
import { DocumentThumbnail } from '@candu-ai/document/preview';
import { SegmentsDefinition } from '@candu-ai/segments/types';
import { Application } from 'commons/types/Application';

export const initialFormValues = {};

export const validationSchema = Yup.object().shape({
  workspaceField: Yup.string().required('Please select a workspace.'),
  contentField: Yup.string(),
  segmentFields: Yup.object()
    .required('Please select a segment.')
    .shape({})
    .test(
      'notEmpty',
      'you should select at least one segment to import',
      (value) => value && Object.keys(value).length !== 0,
    ),
});

type WorkspaceItem = { value: string; label: string };

type FormikValues = {
  workspaceField?: string;
  contentField?: string;
  segmentFields?: { [key: string]: string };
};

interface ImportProps {
  applications: Application[];
  contentMetadata: ContentMetadata;
  contentId: string;
  onClose: VoidFunction;
}

type TableSegment = {
  name: string | undefined;
  segmentId: string;
  documentId: number;
  lastModifiedAt?: string;
  index: number;
};
export const ImportWithFormikContext = ({
  applications,
  contentMetadata,
  contentId,
}: ImportProps) => {
  const { values, setFieldValue, setFieldTouched, setValues } = useFormikContext<FormikValues>();
  const [searchContentValue, setSearchContentValue] = useState<string | undefined>();
  const [searchWorkspaceValue, setSearchWorkspaceValue] = useState<string | undefined>();
  const [searchSegmentValue, setSearchSegmentValue] = useState<string | undefined>();
  const [currentOpenSegmentSelect, setCurrentOpenSegmentSelect] = useState<number | undefined>();

  const workspaceOptions: WorkspaceItem[] = useMemo(
    () =>
      applications.map(({ id, name }) => ({
        value: `${id}`,
        label: name,
      })),
    [applications],
  );

  const filteredWorkspaceOptions = useMemo(() => {
    if (!searchWorkspaceValue) {
      return workspaceOptions;
    }
    return workspaceOptions.filter(({ label }: { label: string }) => {
      return label.toLowerCase().includes(searchWorkspaceValue?.toLowerCase());
    });
  }, [workspaceOptions, searchWorkspaceValue]);

  const { getAll: getAllContent } = useCreatePayload(ENDPOINTS.contentMetadata);
  const { getAll: getAllContentSegments, get: getContentSegments } = useCreatePayload(
    ENDPOINTS.contentSegments,
  );
  const { getAll: getAllSegmentDefinitions } = useCreatePayload(ENDPOINTS.segmentsDefinition);

  const contentSegmentForExistingContentPayload = getContentSegments({ id: contentId });
  const segmentDefinitionPayload = getAllSegmentDefinitions({});

  const { data: contentSegmentForExistingContent } = useQuery(
    createCacheKey(contentSegmentForExistingContentPayload),
    () => fetchResource<ContentSegments>(contentSegmentForExistingContentPayload),
  );
  const { data: segmentsDefinitions } = useQuery(createCacheKey(segmentDefinitionPayload), () =>
    fetchResource<SegmentsDefinition[]>(segmentDefinitionPayload),
  );

  const workspaceSegmentList = useMemo(() => {
    if (!contentSegmentForExistingContent || !segmentsDefinitions) {
      return [];
    }

    const documents = contentSegmentForExistingContent.segmentDocuments || [];

    const documentList = segmentsDefinitions.map(({ id, name }) => {
      const document = documents.find(({ segmentId }) => segmentId === id);

      return {
        name,
        segmentId: id,
        documentId: (document && document.documentId) || null,
        segmentExists: !!document,
      };
    });

    const defaultDocument = {
      name: 'Everyone',
      segmentId: 'Everyone', // contentSegments.id,
      documentId: contentSegmentForExistingContent.defaultDocumentId,
      segmentExists: !!contentSegmentForExistingContent.defaultDocumentId,
    };
    return [defaultDocument, ...documentList];
  }, [contentSegmentForExistingContent, segmentsDefinitions]);

  const workspaceSegmentOptions = useMemo(
    () =>
      workspaceSegmentList.map((s) => ({
        value: s.segmentId,
        label: s.name,
        segmentExists: s.segmentExists,
      })),
    [workspaceSegmentList],
  );

  const filteredWorkspaceSegmentOptions = useMemo(() => {
    if (!searchSegmentValue) {
      return workspaceSegmentOptions;
    }
    return workspaceSegmentOptions.filter((s) => {
      return s.label?.toLowerCase().includes(searchSegmentValue?.toLowerCase());
    });
  }, [searchSegmentValue, workspaceSegmentOptions]);

  const getAllContentPayload = getAllContent({
    query: {
      finder: 'byImportContent',
      applicationId: values.workspaceField ?? '',
      contentType: contentMetadata.contentType,
    },
  });

  const { data: contents, isLoading: isFetchingContents } = useQuery(
    createCacheKey(getAllContentPayload),
    () => fetchResource<ContentMetadata[]>(getAllContentPayload),
    {
      enabled: !!values.workspaceField,
    },
  );

  const getAllContentSegmentsPayload = getAllContentSegments({
    query: {
      finder: 'byApplicationIdContentId',
      applicationId: values.workspaceField,
      contentId: values.contentField,
    },
  });

  const getAllSegmentDefinitionsPayload = getAllSegmentDefinitions({
    query: {
      finder: 'byApplicationId',
      id: values.workspaceField,
    },
  });

  const { data: contentSegments } = useQuery(
    createCacheKey(getAllContentSegmentsPayload),
    () => fetchResource<ContentSegments>(getAllContentSegmentsPayload),
    {
      enabled: !!values.workspaceField && !!values.contentField,
    },
  );

  const { data: segmentDefinitionsForSelectedContent } = useQuery(
    createCacheKey(getAllSegmentDefinitionsPayload),
    () => fetchResource<SegmentsDefinition[]>(getAllSegmentDefinitionsPayload),
    {
      enabled: !!values.workspaceField && !!values.contentField,
    },
  );

  const selectedContentSegmentList: TableSegment[] | undefined = useMemo(() => {
    if (!contentSegments || !segmentDefinitionsForSelectedContent) {
      return undefined;
    }

    const documents = contentSegments.segmentDocuments || [];
    const defaultDocument = contentSegments.defaultDocumentId && {
      name: 'Everyone',
      segmentId: contentSegments.id,
      documentId: contentSegments.defaultDocumentId,
      lastModifiedAt: contentSegments.defaultDocumentLastModifiedAt,
      index: 0,
    };

    const documentList = documents.map(({ segmentId, documentId, lastModifiedAt }, index) => {
      const document = segmentDefinitionsForSelectedContent?.find(({ id }) => segmentId === id);

      return {
        name: document?.name,
        segmentId,
        documentId,
        lastModifiedAt,
        index: defaultDocument ? index + 1 : index,
      };
    });

    return defaultDocument ? [defaultDocument, ...documentList] : documentList;
  }, [contentSegments, segmentDefinitionsForSelectedContent]);

  const contentOptions = useMemo(() => contents?.map((c) => ({ value: c.id, label: c.name })), [
    contents,
  ]);
  const filteredContentOptions = useMemo(() => {
    if (!searchContentValue) {
      return contentOptions;
    }
    return contentOptions?.filter(({ label }: { label: string }) => {
      return label.toLowerCase().includes(searchContentValue?.toLowerCase());
    });
  }, [contentOptions, searchContentValue]);

  const updateSearchContentValue = debounce((e) => {
    setSearchContentValue(e.target.value);
  }, 300);

  const updateSearchWorkspaceValue = debounce((e) => {
    setSearchWorkspaceValue(e.target.value);
  }, 300);

  const updateSearchSegmentValue = debounce((e) => {
    setSearchSegmentValue(e.target.value);
  }, 300);

  const getRenderSelect = useCallback(
    (field: any, record: TableSegment) => {
      const value = values.segmentFields?.[record.documentId];
      const overrides = workspaceSegmentOptions.find((ws) => ws.value === value)?.segmentExists;
      return (
        <FormField
          key={record.documentId}
          state={overrides ? 'warning' : 'none'}
          feedback={overrides ? `Segment already exists: the content will be overriden` : ''}
          label=""
        >
          <SelectInput
            usePortal
            scale="small"
            onFilter={(e) => {
              updateSearchSegmentValue(e);
            }}
            onClearFilter={() => setSearchSegmentValue(undefined)}
            searchable
            onOpeningChange={(isOpen) => {
              if (!isOpen && record.documentId === currentOpenSegmentSelect) {
                setSearchSegmentValue(undefined);
                setCurrentOpenSegmentSelect(undefined);
              } else if (isOpen && record.documentId !== currentOpenSegmentSelect) {
                setCurrentOpenSegmentSelect(record.documentId);
              }
            }}
            items={filteredWorkspaceSegmentOptions}
            selectedItem={workspaceSegmentOptions.find((s) => s.value === value)}
            onSelect={(selected) => {
              const newSegmentFields = Object.fromEntries(
                Object.entries({
                  ...values.segmentFields,
                  [record.documentId]: selected?.value,
                })
                  .map(([docKey, segValue]) => (segValue ? [docKey, segValue] : null))
                  .filter(Boolean) as [string, string][],
              );
              setFieldTouched(`segmentFields`, true);
              setFieldValue(`segmentFields`, newSegmentFields);
            }}
          />
        </FormField>
      );
    },
    [
      setFieldTouched,
      currentOpenSegmentSelect,
      values.segmentFields,
      workspaceSegmentOptions,
      updateSearchSegmentValue,
      filteredWorkspaceSegmentOptions,
      setFieldValue,
    ],
  );

  return (
    <Form>
      <Stack spaceAfter="s">
        <Field name="workspaceField">
          {({ field, meta }: FieldAttributes<any>) => (
            <>
              <FormField
                label="Select the workspace to import content from"
                necessity="required"
                state={meta.touched && meta.error ? 'danger' : 'none'}
                feedback={meta.touched ? meta.error : ''}
                m="xs"
              >
                <SelectInput<WorkspaceItem>
                  {...field}
                  items={filteredWorkspaceOptions}
                  searchInputProps={{
                    placeholder: 'Search Workspace',
                  }}
                  usePortal
                  clearable={false}
                  onFilter={updateSearchWorkspaceValue}
                  onClearFilter={() => setSearchWorkspaceValue(undefined)}
                  searchable
                  onOpeningChange={(isOpen) => {
                    if (!isOpen) {
                      setSearchWorkspaceValue(undefined);
                    }
                  }}
                  selectedItem={workspaceOptions.find(({ value }) => value === field.value)}
                  onSelect={(selected) => {
                    setValues({
                      workspaceField: selected?.value,
                    });
                  }}
                />
              </FormField>
            </>
          )}
        </Field>

        <Field name="contentField">
          {({ field, meta }: FieldAttributes<any>) => (
            <>
              <FormField
                label="Select the content to import"
                necessity="required"
                state={meta.touched && meta.error ? 'danger' : 'none'}
                feedback={meta.touched ? meta.error : ''}
                m="xs"
              >
                <SelectInput<WorkspaceItem>
                  {...field}
                  usePortal
                  loading={isFetchingContents}
                  disabled={!values.workspaceField}
                  items={filteredContentOptions}
                  searchInputProps={{
                    placeholder: 'Search Content',
                  }}
                  clearable={false}
                  onFilter={updateSearchContentValue}
                  onClearFilter={() => setSearchContentValue(undefined)}
                  searchable
                  onOpeningChange={(isOpen) => {
                    if (!isOpen) {
                      setSearchContentValue(undefined);
                    }
                  }}
                  selectedItem={contentOptions?.find(({ value }) => value === field.value)}
                  onSelect={(selected) => {
                    setValues((prevValues) => ({
                      workspaceField: prevValues.workspaceField,
                      contentField: selected?.value,
                    }));
                  }}
                />
              </FormField>
            </>
          )}
        </Field>

        {selectedContentSegmentList ? (
          <Field name="segmentFields">
            {({ meta }: FieldAttributes<any>) => (
              <>
                <FormField
                  label="Select which segments to import"
                  necessity="required"
                  state={meta.touched && meta.error ? 'danger' : 'none'}
                  feedback={meta.touched ? meta.error : ''}
                  m="xs"
                >
                  <Table
                    scale="small"
                    selected={Object.keys(values.segmentFields || {})}
                    rows={selectedContentSegmentList}
                    rowSelection={{
                      type: 'hidden',
                      getCheckboxProps: (record) => ({
                        name: `${record.documentId}`,
                      }),
                    }}
                    columns={[
                      {
                        key: 'preview',
                        dataIndex: 'documentId',
                        title: 'Preview',
                        render: (docId: number) => (
                          <DocumentThumbnail
                            width="120px"
                            height="60px"
                            documentId={docId}
                            applicationId={
                              values.workspaceField ? parseFloat(values.workspaceField) : undefined
                            }
                          />
                        ),
                      },
                      {
                        key: 'name',
                        dataIndex: 'name',
                        title: 'Segment Name',
                      },
                      {
                        key: 'remap',
                        dataIndex: 'remap',
                        title: 'Remap Into',
                        render: getRenderSelect,
                      },
                    ]}
                  />
                </FormField>
              </>
            )}
          </Field>
        ) : null}
      </Stack>
    </Form>
  );
};

const Import = (props: ImportProps) => {
  const { action } = useActions(ENDPOINTS.contentMetadata);
  const { contentId, onClose } = props;
  const onSubmit = useCallback(
    (values: FormikValues) => {
      const importMappings = Object.entries(values.segmentFields || {}).map(([key, value]) => ({
        documentId: parseInt(key, 10),
        segmentId: value,
      }));
      return action({
        name: 'importContentFromApplication',
        body: {
          fromContentId: values.contentField,
          toContentId: contentId,
          segmentDocumentMap: importMappings,
        },
        invalidates: {
          [ENDPOINTS.contentMetadata]: { id: contentId },
          [ENDPOINTS.contentSegments]: { id: contentId },
        },
        onSuccessNotification: {
          message: 'Content Successfully Imported',
        },
      }).then(onClose);
    },
    [action, contentId, onClose],
  );

  const formik = useFormik({
    initialValues: initialFormValues,
    validationSchema,
    onSubmit,
  });

  return (
    <FormikProvider value={formik}>
      <Modal modalWidth="large" onClose={props.onClose} severity="Info">
        <Modal.Header title="Import Content" />
        <Modal.Body>
          <ImportWithFormikContext {...props} />
        </Modal.Body>
        <Modal.Footer
          primaryAction={{
            loading: formik.isSubmitting,
            disabled: !formik.dirty || !formik.isValid,
            onClick: () => formik.handleSubmit(),
            children: 'Import Content now!',
          }}
        />
      </Modal>
    </FormikProvider>
  );
};

const withApplications = connectWithHammock(() => ({
  endpoint: ENDPOINTS.applications,
  resourceName: 'applications',
}));

export default compose(withApplications)(Import);
